import React, { FC } from 'react';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

interface AvailableLanguage {
  href: string;
  label: string;
}

interface Props {
  availableLanguages: AvailableLanguage[];
}

const FrVersion: FC<Props> = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='politique-biometrique-et-decharge-de-responsabilite'>
        Politique biométrique et décharge de responsabilité
      </h4>

      <p>Langues disponibles:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Dernière mise à jour le : 20 février 2026</p>
      <p>
        La Politique biométrique et décharge de responsabilité de Plaid («
        Politique biométrique ») décrit la manière dont Plaid Inc. et ses
        filiales, Plaid Financial Ltd. et Plaid, B.V. (collectivement, « Plaid
        ») traitent les photos et/ou les images vidéo de votre visage, y compris
        les numérisations de la géométrie faciale extraites de ces images, qui
        peuvent être considérées comme des données biométriques dans certaines
        juridictions.
      </p>
      <p>
        Plaid agit en tant que prestataire de services pour nos clients dans le
        cadre d'une prestation de services de vérification de l'identité et de
        prévention de la fraude. Pour fournir nos services de vérification de
        l'identité et/ou de prévention de la fraude, nos clients sont
        susceptibles de vous demander de fournir à Plaid, et/ou à ses
        prestataires de services, un document d'identité renfermant votre image
        et/ou une photo ou une image vidéo de vous-même.
      </p>
      <p>
        Plaid et/ou ses prestataires de services collectent et traitent l'image
        à partir de votre document d'identité et de votre photo ou image vidéo,
        sur instruction de nos clients, pour numériser votre géométrie faciale à
        l'aide de la reconnaissance faciale ou d'une technologie similaire, afin
        de fournir à nos clients des services de vérification de l'identité et
        de prévention de la fraude. Plaid et/ou ses prestataires de services
        peuvent également traiter et utiliser votre photo ou image vidéo dans le
        cadre des services de détection et de prévention de la fraude, y compris
        pour déterminer si votre photo ou image vidéo représente une personne
        réelle. L'image, la photo ou l'image vidéo de votre document d'identité,
        ainsi que la numérisation de la géométrie faciale dérivée de ces images,
        peuvent être considérées comme des informations biométriques dans
        certaines juridictions.
      </p>
      <p>
        Plaid et/ou ses prestataires de services recueilleront, traiteront,
        obtiendront, utiliseront et stockeront l'image, la photo ou l'image
        vidéo de votre pièce d'identité dans le but de :
      </p>
      <ol>
        <li>
          comparer la géométrie faciale dérivée de l'image qui figure sur votre
          document d'identité à la géométrie faciale dérivée de la photo ou
          l'image vidéo que vous fournissez pour aider à vérifier votre identité
          ;
        </li>

        <li>
          détecter et prévenir la fraude, y compris en déterminant votre âge
          estimatif, le cas échéant, à l'aide d'une image de votre document
          d'identité, une image d'une photo ou une image vidéo de vous-même,
          et/ou de votre géométrie faciale dérivée ; et
        </li>

        <li>
          maintenir ou fournir nos services à nos clients, y compris en les
          aidant à évaluer les sessions de vérification d'identité.
        </li>
      </ol>
      <p>
        Plaid et ses prestataires de services stockent pour le compte de ses
        clients, dans un format crypté, l'image de votre pièce d'identité, de
        votre photo ou des images vidéo de votre visage et votre géométrie
        faciale dérivée, le cas échéant.
      </p>
      <p>
        PLAID ET SES PRESTATAIRES DE SERVICES N'UTILISENT JAMAIS LES
        INFORMATIONS PERSONNELLES OU L'IMAGE EXTRAITE DE VOTRE DOCUMENT
        D'IDENTITÉ, PHOTO OU IMAGE VIDÉO, Y COMPRIS TOUTE NUMÉRISATION DE LA
        GÉOMÉTRIE FACIALE DÉRIVÉE ASSOCIÉE À VOS IMAGES, POUR RENFORCER,
        AMÉLIORER, OU DÉVELOPPER SES SERVICES.
      </p>
      <p>
        Si vous ne fournissez pas de document d'identité avec une image de votre
        visage ou une photo et une image vidéo de vous-même, Plaid et ses
        prestataires de services ne traiteront pas vos images pour dériver vos
        données relatives à votre géométrie faciale afin de comparer votre
        document d'identité à votre photo ou image vidéo. Plaid conservera un
        enregistrement de votre processus de vérification partiel conformément
        aux instructions du client, sauf disposition contraire de la loi ou
        d'une procédure légale indiquant de supprimer ces données après une
        période plus courte. Pour plus d'informations, consultez notre{' '}
        <Link href='#idv-privacy-statement'>
          <a>Déclaration de confidentialité IDV</a>
        </Link>
        .
      </p>
      <p>
        <strong>Conservation et stockage</strong>
      </p>
      <p>
        Lorsque Plaid agit en prestataire de services, tel que défini par la loi
        applicable, nos clients demandent à Plaid de traiter, stocker, conserver
        ou supprimer votre pièce d'identité et/ou votre photo ou image vidéo.
        Plaid et ses prestataires de services détruiront et supprimeront
        définitivement tout document d'identité et/ou photographie ou image
        vidéo conformément aux instructions de notre client. Plaid stockera les
        documents d'identité et/ou les photos ou images vidéo que vous
        fournissez pour la durée de la relation commerciale entre Plaid et le
        client pour laquelle les images ont été collectées et conservées, sauf
        disposition contraire de la loi ou d'une procédure légale demandant de
        supprimer ces données après une période plus courte.
      </p>
      <p>
        Si vous avez créé un Compte Plaid soumis aux «{' '}
        <Link href='https://plaid.com/legal/#plaid-account-terms'>
          <a>Plaid Account Terms</a>
        </Link>{' '}
        » et enregistré vos informations de vérification d'identité, Plaid peut
        stocker à votre demande un ou plusieurs documents d'identité renfermant
        des images du visage que vous fournissez. Plaid ne traite jamais l'image
        à partir de ces documents d'identité pour des finalités supplémentaires,
        sauf si vous le lui demandez, lorsque vous vérifiez avec une nouvelle
        application ou un nouveau service, et Plaid ne dérive jamais vos données
        de géométrie faciale à partir de documents d'identité stockés dans votre
        Compte Plaid. Vous pouvez en savoir plus sur la façon de supprimer vos
        documents d'identité stockés dans votre compte Plaid en contactant{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        ou en créant un compte « Plaid Portal » à l'adresse{' '}
        <Link href='https://my.plaid.com/'>
          <a>my.plaid.com</a>
        </Link>
        . Nonobstant toute indication contraire pouvant figurer dans la{' '}
        <Link href='https://plaid.com/legal/#end-user-privacy-policy'>
          <a>Politique de confidentialité destinée aux utilisateurs finaux</a>
        </Link>{' '}
        de Plaid ou les «{' '}
        <Link href='https://plaid.com/legal/#plaid-account-terms'>
          <a>Plaid Account Terms</a>
        </Link>{' '}
        », la présente Politique biométrique prévaudra en cas de divergence avec
        les autres conditions ou avis qui vous sont fournis.
      </p>
      <p>
        <strong>Conditions supplémentaires</strong>
      </p>
      <p>
        Plaid et/ou ses prestataires de services utilisent et traitent votre
        image à partir de votre document d'identité et/ou photo ou image vidéo
        en se conformant strictement à la présente Politique biométrique, et
        pour aucune autre raison. Plaid ne vend pas d'informations biométriques.
        Plaid n'est pas responsable des politiques ou pratiques de ses clients.
        Veuillez consulter la{' '}
        <Link href='#idv-privacy-statement'>
          <a>Déclaration de confidentialité IDV</a>
        </Link>{' '}
        de Plaid pour comprendre comment nous collectons et utilisons vos
        Informations personnelles pour le compte de nos clients. Plaid vous
        encourage également à examiner les politiques de confidentialité de nos
        clients et toutes les conditions générales qui peuvent s'appliquer à
        leur utilisation de vos données, dont certaines peuvent être considérées
        comme des données biométriques dans votre région.
      </p>
      <p>
        En utilisant les services Plaid de vérification de l'identité et de
        prévention de la fraude pour vous connecter à une application ou à un
        service fourni par nos clients, vous convenez que tous les documents
        d'identité ou photos et images vidéo que vous fournirez pour vérifier
        votre identité auprès de nos clients seront : 1) authentiques ; 2) que
        les documents d'identité soumis seront vos propres documents d'identité
        délivrés par l'administration ; et 3) que toute pièce d'identité, photo
        ou image vidéo fournie représentera avec précision votre visage et
        uniquement votre visage sur la photo ou l'image vidéo que vous
        fournirez.
      </p>
      <p>
        Vous reconnaissez avoir lu, compris et que vous consentez volontairement
        à la présente Politique biométrique et de décharge de responsabilité ;
        que vous dégagez de toute responsabilité Plaid et ses prestataires de
        services, concernant toute réclamation liée à la collecte, au
        traitement, à l'utilisation, à la divulgation ou au partage de votre
        pièce d'identité et de votre photo ou image vidéo, y compris toute
        donnée dérivée de ces images pour créer des numérisations de géométrie
        de votre visage ; et vous confirmez que vous n'accéderez pas aux
        services de Plaid dans les juridictions où ces services sont interdits
        par la loi en vigueur.
      </p>
      <hr className='section-divider' />
    </>
  );
};

export default FrVersion;
